import { ScrollingModule } from '@angular/cdk/scrolling'

import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  signal
} from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatRippleModule } from '@angular/material/core'
import { MatIconModule } from '@angular/material/icon'
import { PalantirButtonComponent, PalantirModalService, PalantirTooltipModule } from '@fieldcontrol/palantir'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { Subject, debounceTime, fromEvent, takeUntil } from 'rxjs'
import { DetailsSectionComponent } from './sections/details-section/details-section.component'
import { AssetsTreeFiltersModalComponent } from './sections/tree-section/components/filters-modal/filters-modal.component'
import { TreeViewItemTypeEnum } from './sections/tree-section/components/tree-view/tree-view.type'
import { TreeSectionComponent } from './sections/tree-section/tree-section.component'
import { AssetsTreeFilterService } from './services/assets-tree-filter.service'
import { AssetsTreeService } from './services/assets-tree.service'
import { fadeInOut } from '../../core/animations'
import { FcMenuComponent } from '../../core/components/fc-menu/fc-menu.component'
import { FcMenuService } from '../../core/components/fc-menu/fc-menu.service'
import { FcMenuOptionComponent } from '../../core/components/fc-menu-option/fc-menu-option.component'
import { IconsComponent } from '../../core/icons/icons.component'
import { GlobalEventsService } from '../../core/services/global-events/global-events.service'
import { GlobalEventNameEnum } from '../../core/services/global-events/global-events.types'

@Component({
  selector: 'app-assets-tree',
  templateUrl: './assets-tree.component.html',
  styleUrls: ['./assets-tree.component.scss', '../../../styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  animations: [
    fadeInOut
  ],
  imports: [
    FcMenuComponent,
    FcMenuOptionComponent,
    IconsComponent,
    MatButtonModule,
    MatIconModule,
    MatRippleModule,
    NgScrollbarModule,
    ScrollingModule,
    TreeSectionComponent,
    DetailsSectionComponent,
    PalantirButtonComponent,
    PalantirTooltipModule,
    TranslateModule
],
  schemas: []
})
export class AssetsTreeComponent implements OnInit, OnDestroy {
  @Input()
  public hasLocationCreatePermission = false

  @Input()
  public hasEquipmentCreatePermission = false

  @Input()
  public hasLocationEditPermission = false

  @Input()
  public hasEquipmentEditPermission = false

  @Input()
  public hasEquipmentViewPermission = false

  @Input()
  public hasMaintenanceViewPermission = false

  public showDetailsBody = signal(false)
  public reloadTree$ = new Subject<void>()
  public subscriptions$ = new Subject<void>()

  public fcMenuService = inject(FcMenuService)
  public assetsTreeService = inject(AssetsTreeService)
  public globalEventsService = inject(GlobalEventsService)
  public assetsTreeFilterService = inject(AssetsTreeFilterService)
  private palantirModalService = inject(PalantirModalService)

  public async ngOnInit(): Promise<void> {
    this.setPermissions()
    this.assetsTreeFilterService.reset()
    this.assetsTreeFilterService.clearForm()

    this.reloadTree$
      .pipe(debounceTime(500))
      .subscribe(async () => {
        this.setPermissions()
        await this.assetsTreeFilterService.search()
      })

    this.assetsTreeService.showDetails$
      .pipe(takeUntil(this.subscriptions$))
      .subscribe(() => {
        this.showDetailsBody.set(true)
      })

    this.assetsTreeService.closeDetails$
      .pipe(takeUntil(this.subscriptions$))
      .subscribe(() => {
        this.showDetailsBody.set(false)
      })

    fromEvent(window, GlobalEventNameEnum.Created)
      .pipe(takeUntil(this.subscriptions$))
      .subscribe((event: any) => {
        const details = event.detail || {}
        if (details.type === TreeViewItemTypeEnum.Location) {
          this.reloadTree$.next()
        }
      })

    fromEvent(window, 'equipment-closed')
      .pipe(takeUntil(this.subscriptions$))
      .subscribe(async () => {
        this.reloadTree$.next()
      })

    this.reloadTree$.next()
  }

  public ngOnDestroy(): void {
    this.subscriptions$.next()
    this.subscriptions$.complete()
  }

  public openCreateLocationDialog(): void {
    if (this.hasLocationCreatePermission) {
      this.globalEventsService.dispatchEvent(GlobalEventNameEnum.OpenDialog, { dialog: 'location' })
    }
  }

  public openCreateEquipmentDialog(): void {
    if (this.hasEquipmentCreatePermission) {
      this.globalEventsService.dispatchEvent(GlobalEventNameEnum.OpenDialog, { dialog: 'equipment' })
    }
  }

  private setPermissions(): void {
    this.assetsTreeService.hasLocationEditPermission.set(this.hasLocationEditPermission)
    this.assetsTreeService.hasEquipmentEditPermission.set(this.hasEquipmentEditPermission)
    this.assetsTreeService.hasEquipmentViewPermission.set(this.hasEquipmentViewPermission)
    this.assetsTreeService.hasLocationCreatePermission.set(this.hasLocationCreatePermission)
    this.assetsTreeService.hasEquipmentCreatePermission.set(this.hasEquipmentCreatePermission)
    this.assetsTreeService.hasMaintenanceViewPermission.set(this.hasMaintenanceViewPermission)
  }

  public openFilterAssetsTree(): void {
    this.palantirModalService.openModal(AssetsTreeFiltersModalComponent, {
      size: 'small',
    })
  }
}
